import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Loader } from "components";
import { useCommunity } from 'components/CommunityContext';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

const CardForm = forwardRef(({ onSubmit, isError, errorText }: { onSubmit: (values: any) => void, isError: boolean, errorText: string }, ref: React.Ref<any>) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardSuccess, setCardSuccess] = useState<string | null>(null);
    
    const stripe = useStripe();
    const elements = useElements();
    
    const { theme } = useCommunity();

    useEffect(() => {
      if(stripe && elements){
        setLoading(false);
      }
    }, [stripe, elements]);
    
    const processPayment = async () => {
      if (!stripe || !elements) {
        setLoading(false)
        return null;
      }
      
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        return null;
      }
      
      setIsProcessing(true);
      setCardError(null);
      setCardSuccess(null);
      
      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        
        if (error) {
          setCardError(error.message || 'Processing card error');
          setIsProcessing(false);
          return null;
        }
        
        if (paymentMethod) {
          return paymentMethod.id;
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsProcessing(false);
      }
      
      return null;
    };
    
    useImperativeHandle(ref, () => ({
      processPayment
    }));
    
    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      const paymentMethodId = await processPayment();
      if (paymentMethodId) {
        onSubmit(paymentMethodId);
      }
    };
    
    const cardElementOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: theme.text,
          family: theme.fontBody,
        },
        invalid: {
          color: theme.error,
          iconColor: theme.error,
        },
      },
    };

    if(isLoading){
      return <Loader />
    }
    
    return (
      <form onSubmit={handleSubmit} className="card-form">
        <div className="card-element-container">
          <CardElement options={cardElementOptions} />
        </div>
        {(cardError || isError) && (
          <div className="input-label-error">{cardError || errorText}</div>
        )}
      </form>
    );
  });

  export default CardForm;
